import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { Subscription, filter } from 'rxjs';
import { SelectedTenantService } from '../../../services/selected-tenant.service';

@Component({
  selector: 'app-sidebar',
  imports: [RouterLink, RouterLinkActive],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss'
})
export class SidebarComponent implements OnInit, OnDestroy {
  navItems = [
    { icon: 'bi-grid', label: 'Dashboard', route: '/' },
    { icon: 'bi-people', label: 'Users', route: '/users' }
  ];

  activeTenantSlug: string | null = null;
  tenantExpanded = false;

  private routerSub?: Subscription;

  constructor(private router: Router, private selectedTenantService: SelectedTenantService) {}

  ngOnInit(): void {
    this.syncFromUrl(this.router.url);
    this.routerSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(e => this.syncFromUrl(e.urlAfterRedirects));
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
  }

  private extractSlug(url: string): string | null {
    const match = url.match(/^\/tenants\/([^/]+)/);
    return match ? match[1] : null;
  }

  private syncFromUrl(url: string): void {
    const slug = this.extractSlug(url);

    if (slug && !this.activeTenantSlug) {
      // Entering a tenant: auto-expand, with a tick delay so the open animation plays.
      this.activeTenantSlug = slug;
      this.tenantExpanded = false;
      setTimeout(() => (this.tenantExpanded = true));
    } else if (slug && this.activeTenantSlug && slug !== this.activeTenantSlug) {
      // Switched to a different tenant: keep the section open, just repoint it.
      this.activeTenantSlug = slug;
    } else if (!slug) {
      // Left tenant context entirely.
      this.activeTenantSlug = null;
      this.tenantExpanded = false;
    }
  }

  get activeTenantName(): string {
    const tenant = this.selectedTenantService.tenant();
    return tenant && tenant.tenant_uid === this.activeTenantSlug ? tenant.name : 'Tenant';
  }

  toggleTenantSection(): void {
    if (this.activeTenantSlug) {
      this.tenantExpanded = !this.tenantExpanded;
    }
  }

  get tenantChildren() {
    const slug = this.activeTenantSlug;
    return [
      { label: 'Overview', icon: 'bi-house', route: `/tenants/${slug}`, exact: true },
      { label: 'Services', icon: 'bi-hdd-network', route: `/tenants/${slug}/services`, exact: false },
      { label: 'Security (SSO)', icon: 'bi-shield-lock', route: `/tenants/${slug}/sso`, exact: false },
      { label: 'Users', icon: 'bi-people', route: `/tenants/${slug}/users`, exact: false },
      { label: 'Roles', icon: 'bi-diagram-3', route: `/tenants/${slug}/roles`, exact: false }
    ];
  }
}
